import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";
import { getArticleBySlug, defaultArticles } from "@/mocks/seoArticle";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const siteUrl = (): string => {
    if (typeof import.meta !== "undefined" && import.meta.env?.VITE_SITE_URL) {
      return import.meta.env.VITE_SITE_URL as string;
    }
    return "https://hipobuyspreadsheets.site";
  };

  const seoData = article
    ? {
        title: article.metaTitle,
        description: article.metaDescription,
        keywords:
          "hipobuy spreadsheet, gestion financière, budget personnel, suivi dépenses, hipobuy budgeting spreadsheet",
        canonicalPath: `/article/${article.slug}`,
        ogType: "article",
        ogImage: article.imageUrl,
        jsonLd: {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.metaDescription,
          url: `${siteUrl()}/article/${article.slug}`,
          image: article.imageUrl,
          inLanguage: "fr",
          datePublished: "2026-06-15",
          dateModified: "2026-06-23",
          author: {
            "@type": "Organization",
            name: "Hipobuy Spreadsheet",
            url: siteUrl(),
          },
          publisher: {
            "@type": "Organization",
            name: "Hipobuy Spreadsheet",
            url: siteUrl(),
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${siteUrl()}/article/${article.slug}`,
          },
        },
      }
    : {
        title: "Article introuvable | Hipobuy Spreadsheet",
        description:
          "L&apos;article que vous cherchez n&apos;existe pas ou a été déplacé. Retournez à l&apos;accueil pour découvrir nos guides financiers.",
        canonicalPath: `/article/${slug || "inconnu"}`,
        ogType: "website",
        jsonLd: null,
      };

  useSEO(seoData);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-6">
            <h1 className="text-3xl font-bold text-foreground-950 font-heading mb-4">
              Article introuvable
            </h1>
            <p className="text-foreground-500 font-sans mb-6">
              L&apos;article que vous cherchez n&apos;existe pas ou a été déplacé.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-foreground-950 text-background-50 font-sans text-sm font-medium px-6 py-3 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Retour à l&apos;accueil
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[480px] md:min-h-[560px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
              title={article.title}
            />
          </div>
          <div className="absolute inset-0 z-[1] bg-foreground-950/45"></div>

          <div className="relative z-10 w-full px-6 md:px-12 py-24 md:py-32">
            <div className="max-w-[1400px] mx-auto">
              <nav className="mb-8" aria-label="Fil d'Ariane">
                <p className="text-background-50/70 text-xs md:text-sm font-sans">
                  <Link to="/" className="hover:text-background-50 transition-colors">
                    Accueil
                  </Link>
                  <span className="mx-2">&gt;</span>
                  <span className="text-background-50/90">Blog</span>
                  <span className="mx-2">&gt;</span>
                  <span className="text-background-50">Hipobuy Spreadsheet</span>
                </p>
              </nav>

              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
                <div className="max-w-3xl">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl text-background-50 font-bold font-heading leading-[1.1]">
                    <span className="font-heading">Hipobuy Spreadsheet</span>
                    <span className="block italic font-light mt-2 md:mt-3 text-background-50/90">
                      {article.title.replace("Hipobuy Spreadsheet", "").replace(/\s*:\s*/, "").replace(/\s+/g, " ").trim()}
                    </span>
                  </h1>
                </div>
                <div className="lg:max-w-xs">
                  <p className="text-sm text-background-50/70 font-sans leading-relaxed">
                    {article.intro.slice(0, 150)}...
                  </p>
                </div>
              </div>

              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-8 bg-background-50/90 text-foreground-950 font-sans text-sm font-medium px-5 py-2.5 rounded-full hover:bg-background-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Retour à l&apos;accueil
              </Link>
            </div>
          </div>
        </section>

        {/* Article body */}
        <section className="py-20 md:py-28 px-6 md:px-12 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-12 md:gap-20">
              <div className="lg:w-44 flex-shrink-0">
                <p className="text-xs font-semibold tracking-widest text-foreground-400 uppercase font-sans">
                  Guide SEO 2026
                </p>
              </div>

              <div className="flex-1 max-w-3xl">
                <p className="text-base md:text-lg text-foreground-700 font-sans leading-relaxed mb-10">
                  {article.intro}
                </p>

                {article.sections.map((section, idx) => (
                  <article key={idx} className="mb-12 md:mb-16">
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
                      {section.title}
                    </h2>
                    {section.content && (
                      <div className="text-base md:text-lg text-foreground-700 font-sans leading-relaxed space-y-4">
                        {section.content.split("\n\n").map((para, pi) => (
                          <p key={pi}>{para}</p>
                        ))}
                      </div>
                    )}

                    {section.comparisonTable && (
                      <div className="mt-8 mb-6 overflow-x-auto">
                        <table className="w-full text-sm font-sans border-collapse">
                          <thead>
                            <tr className="border-b border-background-300/60">
                              <th className="text-left py-3 px-4 text-foreground-800 font-semibold whitespace-nowrap">Outil</th>
                              <th className="text-left py-3 px-4 text-foreground-800 font-semibold whitespace-nowrap">Facilité</th>
                              <th className="text-left py-3 px-4 text-foreground-800 font-semibold whitespace-nowrap">Automatisation</th>
                              <th className="text-left py-3 px-4 text-foreground-800 font-semibold whitespace-nowrap">Coût</th>
                              <th className="text-left py-3 px-4 text-foreground-800 font-semibold whitespace-nowrap">Personnalisation</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-background-200/60 bg-primary-50/50">
                              <td className="py-3 px-4 text-foreground-950 font-semibold whitespace-nowrap">Hipobuy Spreadsheet</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐⭐⭐</td>
                              <td className="py-3 px-4 text-primary-600 font-semibold whitespace-nowrap">Gratuit</td>
                              <td className="py-3 px-4 text-foreground-700">Très élevé</td>
                            </tr>
                            <tr className="border-b border-background-200/60">
                              <td className="py-3 px-4 text-foreground-800 whitespace-nowrap">Excel</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-600 whitespace-nowrap">Gratuit</td>
                              <td className="py-3 px-4 text-foreground-700">Moyen</td>
                            </tr>
                            <tr className="border-b border-background-200/60">
                              <td className="py-3 px-4 text-foreground-800 whitespace-nowrap">Google Sheets</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-600 whitespace-nowrap">Gratuit</td>
                              <td className="py-3 px-4 text-foreground-700">Élevé</td>
                            </tr>
                            <tr className="border-b border-background-200/60">
                              <td className="py-3 px-4 text-foreground-800 whitespace-nowrap">YNAB</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-600 whitespace-nowrap">Payant</td>
                              <td className="py-3 px-4 text-foreground-700">Moyen</td>
                            </tr>
                            <tr className="border-b border-background-200/60">
                              <td className="py-3 px-4 text-foreground-800 whitespace-nowrap">Mint</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-600 whitespace-nowrap">Gratuit</td>
                              <td className="py-3 px-4 text-foreground-700">Faible</td>
                            </tr>
                            <tr>
                              <td className="py-3 px-4 text-foreground-800 whitespace-nowrap">Notion finance</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-700">⭐⭐</td>
                              <td className="py-3 px-4 text-foreground-600 whitespace-nowrap">Gratuit</td>
                              <td className="py-3 px-4 text-foreground-700">Élevé</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                    {idx === 1 && (
                      <div className="mt-8 p-5 rounded-2xl bg-accent-100/80 border border-accent-200/60">
                        <Link
                          to="/"
                          className="inline-flex items-center gap-2 text-accent-800 hover:text-accent-900 font-sans text-sm font-medium transition-colors"
                        >
                          <i className="ri-home-line"></i>
                          Découvrez la page d&apos;accueil Hipobuy Spreadsheet
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    )}

                    {section.isFAQ && section.faqs && (
                      <div className="mt-8 space-y-5">
                        {section.faqs.map((faq, fi) => (
                          <div key={fi} className="p-5 rounded-2xl bg-background-100 border border-background-200/60">
                            <h3 className="text-foreground-950 font-semibold font-sans text-sm md:text-base mb-2">
                              Q: {faq.q}
                            </h3>
                            <p className="text-foreground-600 font-sans text-sm md:text-base leading-relaxed">
                              R: {faq.a}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                ))}

                <div className="mt-12 pt-10 border-t border-background-300/60">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 dark:text-foreground-950 font-sans text-sm md:text-base font-medium px-7 py-3.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {article.cta}
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 md:py-20 px-6 md:px-12 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              <div>
                <span className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">{article.sections.length}+</span>
                <p className="text-sm text-foreground-500 font-sans mt-1">Chapitres du guide</p>
              </div>
              <div>
                <span className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">8min</span>
                <p className="text-sm text-foreground-500 font-sans mt-1">Temps de lecture</p>
              </div>
              <div>
                <span className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">100%</span>
                <p className="text-sm text-foreground-500 font-sans mt-1">Gratuit</p>
              </div>
              <div>
                <span className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">2026</span>
                <p className="text-sm text-foreground-500 font-sans mt-1">Dernière mise à jour</p>
              </div>
            </div>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-20 md:py-28 px-6 md:px-12 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground-950 font-heading mb-12 md:mb-16">
              <span className="font-heading">Articles</span>
              <span className="block italic font-light text-foreground-600">recommandés</span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {defaultArticles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="rounded-3xl border border-background-200/60 p-6 md:p-7 hover:border-accent-200/60 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-accent-100/80 flex items-center justify-center mb-4">
                      <i className="ri-article-line text-accent-600"></i>
                    </div>
                    <h3 className="text-foreground-950 font-semibold font-sans text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {related.title}
                    </h3>
                    <p className="text-foreground-500 font-sans text-xs md:text-sm leading-relaxed mb-4 line-clamp-2">
                      {related.intro.slice(0, 100)}...
                    </p>
                    <span className="inline-flex items-center gap-1 text-accent-600 hover:text-accent-700 font-sans text-xs font-medium transition-colors">
                      Lire l&apos;article
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}